/**
 * Fake demo data served while an interactive tutorial is running, so walkthroughs
 * have a populated floor plan, table orders, and POS menu to point at even on a
 * brand-new restaurant with no real data.
 *
 * Two injection sites read from here (both no-ops in normal operation):
 *  - the Tables page swaps `tables`/`floorPlans`/`todaysReservations` (floor view)
 *  - the `api` layer swaps `getServices`/`getProducts`/`getFoodTypes`/`getTablesMeta`/
 *    `getTableOrders` (POS catalog + table-detail orders)
 *
 * The non-Vue `api` layer can't call `useTraining()`, so activation is bridged via
 * the module-level reactive flag `activeTutorialDataId`, which `TrainingProvider`
 * sets whenever the active tutorial is one of `TUTORIAL_DATA_IDS`.
 */
import { ref } from "vue";
import { localizeText, type LocalizedText, type TutorialOverlayId } from "@lemmonhq/knowledgebase";
import type {
  Category,
  FloorPlan,
  FloorPlanTable,
  FoodType,
  Order,
  OrderItem,
  OrderItemTaxRate,
  PaymentDetails,
  Product,
  Reservation,
  Service,
  SuccesfullPartialPayment,
  TableStatus,
  UserProfile,
} from "@lemmonhq/types";

// ---------------------------------------------------------------------------
// Activation flag
// ---------------------------------------------------------------------------

/** Tutorials that should see the fake floor plan + menu instead of real shop data. */
export const TUTORIAL_DATA_IDS = new Set<string>([
  "tables.full-workflow",
  "tables.payment-workflow",
  "tables.order-basics",
  "pos.overview",
  "pos.taking-an-order",
  "pos.add-and-pay",
  "pos.payment-functions",
]);

/** Set by TrainingProvider to the active tutorial id (or null). */
export const activeTutorialDataId = ref<string | null>(null);

/**
 * Mirrors the app locale (set by TrainingProvider, same bridge pattern as
 * `activeTutorialDataId`) so the non-Vue demo-data builders can localize menu
 * names. Read at build time — the POS catalog fetches on mount, and tutorials
 * navigate into the POS, so a fresh mount picks up the current locale.
 */
export const tutorialLocale = ref<string>("en");

/** True while a tutorial that wants demo data is active. */
export const isTutorialDataActive = (): boolean =>
  activeTutorialDataId.value !== null && TUTORIAL_DATA_IDS.has(activeTutorialDataId.value);

/** The subset of data-backed tutorials that run on the POS page. */
export const POS_TUTORIAL_IDS = new Set<string>([
  "pos.overview",
  "pos.taking-an-order",
  "pos.add-and-pay",
  "pos.payment-functions",
]);

/** True while a POS-page tutorial is active (gates the POS catalog/cart seeding). */
export const isPosTutorialActive = (): boolean =>
  activeTutorialDataId.value !== null && POS_TUTORIAL_IDS.has(activeTutorialDataId.value);

/**
 * Demo POS catalog ids live in fixed ranges (products 91xxx, categories 92xxx,
 * services 93xxx — see PRODUCT_SPECS / buildMenu). This identifies a demo product
 * so the POS tutorial can strip only its own seeded/added items on teardown, leaving
 * any real cashier items untouched.
 */
export const isTutorialDemoProductId = (id: number | null | undefined): boolean =>
  typeof id === "number" && id >= 91000 && id < 92000;

// ---------------------------------------------------------------------------
// Overlay reconciliation
// ---------------------------------------------------------------------------

/**
 * The tutorial-managed overlay the active step requires, or null. Set by
 * TrainingProvider on each step transition (only for data-backed tutorials).
 * Overlay owners across the Tables/POS pages watch this and close their own
 * overlay unless the active step needs it (or one nested inside it), so each
 * step shows only the modals/sheets it requires.
 */
export const requiredTutorialOverlay = ref<TutorialOverlayId | null>(null);

/**
 * Bumped by TrainingProvider on every step transition (and on tutorial start/exit).
 * Overlay owners watch this — not `requiredTutorialOverlay` alone — so reconciliation
 * (close strays, keep/open the required overlay) runs on EVERY step, including
 * consecutive steps that share the same overlay value (e.g. several catalog steps with
 * no overlay, or three `table-detail` steps in a row).
 */
export const tutorialStepTick = ref(0);

/** The fixed demo table whose detail sheet the tutorial auto-opens for `table-detail`. */
export const TUTORIAL_DETAIL_TABLE_ID = 90101;

/**
 * Overlay nesting: an overlay renders inside its parent, so closing the parent
 * would unmount the child. `table-payment`'s payment sheet lives inside the
 * `table-detail` sheet, so when payment is required the detail sheet must stay open.
 */
const OVERLAY_PARENTS: Partial<Record<TutorialOverlayId, TutorialOverlayId>> = {
  "table-payment": "table-detail",
};

/** True when `descendant` is `ancestor` or nests (transitively) inside it. */
const isOverlayWithin = (
  descendant: TutorialOverlayId | null,
  ancestor: TutorialOverlayId,
): boolean => {
  let current: TutorialOverlayId | undefined = descendant ?? undefined;
  while (current) {
    if (current === ancestor) return true;
    current = OVERLAY_PARENTS[current];
  }
  return false;
};

/**
 * Whether an owner of `id` should keep its overlay open given the step's required
 * overlay: keep it iff the required overlay is this id or nests inside it; otherwise
 * the owner closes its overlay (it's a stray for this step).
 */
export const shouldKeepOverlayOpen = (
  id: TutorialOverlayId,
  required: TutorialOverlayId | null,
): boolean => isOverlayWithin(required, id);

/**
 * Whether the list-row kebab for `tableId` should be force-opened during a tutorial:
 * only for the fixed demo table, and only while a step requires the `table-row-menu`
 * overlay. The Change-table / Reassign-items steps spotlight menu items that otherwise
 * live inside a focus-driven dropdown (which closes when the user clicks Next), so
 * TableRow pins it open for the demo row. See `apps/web/src/vue/pages/Tables/TableRow.tsx`.
 */
export const shouldForceTutorialRowMenu = (
  tableId: number,
  required: TutorialOverlayId | null,
): boolean =>
  tableId === TUTORIAL_DETAIL_TABLE_ID && shouldKeepOverlayOpen("table-row-menu", required);

// ---------------------------------------------------------------------------
// Localized menu names — fake demo strings translated inline (same {en,de,fr,
// it,ka} shape the tutorial YAML uses), resolved via `localizeText`. Proper
// nouns (party names) and brand drinks (Cola, Spaghetti, Crème Brûlée) stay as-is.
// ---------------------------------------------------------------------------

const L = (en: string, de: string, fr: string, it: string, ka: string): LocalizedText => ({
  en,
  de,
  fr,
  it,
  ka,
});

// ---------------------------------------------------------------------------
// Shared constants & helpers
// ---------------------------------------------------------------------------

const RESTAURANT_ID = 0;
const ISO = "2024-01-01T00:00:00.000Z";
const minutesAgoISO = (mins: number) => new Date(Date.now() - mins * 60_000).toISOString();

const FAKE_USER: UserProfile = {
  id: 1,
  name: "Trainer",
  guid: "tut-user",
  email: "trainer@example.com",
  phone: null,
  status: "active",
  staff_type: "waiter",
  restaurant_id: RESTAURANT_ID,
  print_receipt: 0,
};

// ---------------------------------------------------------------------------
// Menu: products → categories → services (localized per locale)
// ---------------------------------------------------------------------------

type ProductSpec = {
  id: number;
  slug: string;
  name: LocalizedText;
  price: string;
  item_type: "drink" | "food";
};

// Stable product specs (one per menu item). The order builders below reference
// these by key, and the per-locale builder turns them into localized Products.
const PRODUCT_SPECS = {
  cola: { id: 91001, slug: "cola", name: L("Cola", "Cola", "Cola", "Cola", "კოლა"), price: "4.50", item_type: "drink" },
  iceTea: { id: 91002, slug: "ice-tea", name: L("Ice Tea", "Eistee", "Thé glacé", "Tè freddo", "ყინულის ჩაი"), price: "4.50", item_type: "drink" },
  water: { id: 91003, slug: "water", name: L("Water", "Wasser", "Eau", "Acqua", "წყალი"), price: "3.50", item_type: "drink" },
  redWine: { id: 91004, slug: "red-wine", name: L("Red Wine", "Rotwein", "Vin rouge", "Vino rosso", "წითელი ღვინო"), price: "7.00", item_type: "drink" },
  whiteWine: { id: 91005, slug: "white-wine", name: L("White Wine", "Weißwein", "Vin blanc", "Vino bianco", "თეთრი ღვინო"), price: "7.00", item_type: "drink" },
  blackTea: { id: 91006, slug: "black-tea", name: L("Black Tea", "Schwarzer Tee", "Thé noir", "Tè nero", "შავი ჩაი"), price: "4.00", item_type: "drink" },
  herbalTea: { id: 91007, slug: "herbal-tea", name: L("Herbal Tea", "Kräutertee", "Tisane", "Tisana", "მცენარეული ჩაი"), price: "4.00", item_type: "drink" },
  soup: { id: 91008, slug: "todays-soup", name: L("Today's Soup", "Tagessuppe", "Soupe du jour", "Zuppa del giorno", "დღის სუპი"), price: "8.50", item_type: "food" },
  salad: { id: 91009, slug: "mixed-salad", name: L("Mixed Salad", "Gemischter Salat", "Salade mixte", "Insalata mista", "შერეული სალათი"), price: "9.50", item_type: "food" },
  fries: { id: 91010, slug: "fries", name: L("Fries", "Pommes frites", "Frites", "Patatine fritte", "კარტოფილი ფრი"), price: "6.50", item_type: "food" },
  spaghetti: { id: 91011, slug: "spaghetti", name: L("Spaghetti", "Spaghetti", "Spaghetti", "Spaghetti", "სპაგეტი"), price: "16.50", item_type: "food" },
  cremeBrulee: { id: 91012, slug: "creme-brulee", name: L("Crème Brûlée", "Crème brûlée", "Crème brûlée", "Crème brûlée", "კრემ-ბრიულე"), price: "8.00", item_type: "food" },
  iceCream: { id: 91013, slug: "ice-cream", name: L("Ice Cream", "Eis", "Glace", "Gelato", "ნაყინი"), price: "6.50", item_type: "food" },
} satisfies Record<string, ProductSpec>;

type ProductKey = keyof typeof PRODUCT_SPECS;

const mkProduct = (spec: ProductSpec, locale: string): Product => ({
  id: spec.id,
  name: localizeText(spec.name, locale),
  weight: null,
  additional_info: null,
  price: spec.price,
  special_price: null,
  type: "product",
  is_custom: 0,
  description: null,
  slug: spec.slug,
  status: "available",
  restaurant_id: RESTAURANT_ID,
  created_at: ISO,
  updated_at: ISO,
  deleted_at: null,
  extra_limit: null,
  product_limit: null,
  color: undefined,
  item_type: spec.item_type,
});

const mkCategory = (
  id: number,
  name: LocalizedText,
  locale: string,
  product_ids: number[],
  color?: string,
): Category => ({
  id,
  name: localizeText(name, locale),
  product_ids,
  restaurant_id: RESTAURANT_ID,
  status: "active",
  order: id,
  created_at: ISO,
  updated_at: ISO,
  deleted_at: null,
  color,
});

const mkService = (
  id: number,
  name: LocalizedText,
  locale: string,
  categories: Category[],
  color?: string,
): Service => ({
  id,
  name: localizeText(name, locale),
  restaurant_id: RESTAURANT_ID,
  order: id,
  hide_unavailable: null,
  visible_only_to_staff: null,
  status: "active",
  type: "dine_in",
  created_at: ISO,
  updated_at: ISO,
  deleted_at: null,
  description: null,
  color,
  categories,
  // `days: null` → always passes filterServicesBySchedule (no time gating).
  days: null,
});

// Localized service & category labels.
const SVC_DRINKS = L("Drinks", "Getränke", "Boissons", "Bevande", "სასმელები");
const SVC_FOOD = L("Food", "Speisen", "Plats", "Cibo", "საკვები");
const CAT_DRINKS = SVC_DRINKS;
const CAT_WINE = L("Wine", "Wein", "Vin", "Vino", "ღვინო");
const CAT_TEA = L("Tea", "Tee", "Thé", "Tè", "ჩაი");
const CAT_STARTERS = L("Starters", "Vorspeisen", "Entrées", "Antipasti", "წინაკერძები");
const CAT_MAINS = L("Mains", "Hauptgerichte", "Plats principaux", "Primi", "ძირითადი კერძები");
const CAT_DESSERTS = L("Desserts", "Desserts", "Desserts", "Dolci", "დესერტები");

type TutorialMenu = {
  products: Product[];
  services: Service[];
  productByKey: Record<ProductKey, Product>;
};

const buildMenu = (locale: string): TutorialMenu => {
  const productByKey = {} as Record<ProductKey, Product>;
  (Object.keys(PRODUCT_SPECS) as ProductKey[]).forEach((k) => {
    productByKey[k] = mkProduct(PRODUCT_SPECS[k], locale);
  });
  const pid = (k: ProductKey) => productByKey[k].id;
  const services: Service[] = [
    mkService(
      93001,
      SVC_DRINKS,
      locale,
      [
        mkCategory(92001, CAT_DRINKS, locale, [pid("cola"), pid("iceTea"), pid("water")], "#2563eb"),
        mkCategory(92002, CAT_WINE, locale, [pid("redWine"), pid("whiteWine")], "#7c3aed"),
        mkCategory(92003, CAT_TEA, locale, [pid("blackTea"), pid("herbalTea")], "#0d9488"),
      ],
      "#2563eb",
    ),
    mkService(
      93002,
      SVC_FOOD,
      locale,
      [
        mkCategory(92004, CAT_STARTERS, locale, [pid("soup"), pid("salad")], "#16a34a"),
        mkCategory(92005, CAT_MAINS, locale, [pid("fries"), pid("spaghetti")], "#dc2626"),
        mkCategory(92006, CAT_DESSERTS, locale, [pid("cremeBrulee"), pid("iceCream")], "#db2777"),
      ],
      "#d97706",
    ),
  ];
  return { products: Object.values(productByKey), services, productByKey };
};

// Cache per locale so repeated reads within a render keep stable object refs.
const menuCache = new Map<string, TutorialMenu>();
const getMenu = (locale: string): TutorialMenu => {
  let menu = menuCache.get(locale);
  if (!menu) {
    menu = buildMenu(locale);
    menuCache.set(locale, menu);
  }
  return menu;
};

// Return shallow copies (matching the old `[...TUTORIAL_*]` semantics) so a
// caller that sorts/reverses in place can't corrupt the per-locale cache. The
// inner Product/Service objects stay shared, so order items still reference the
// same localized products as the catalog.
/** Localized POS catalog products for the active app locale. */
export const getTutorialProducts = (): Product[] => [...getMenu(tutorialLocale.value).products];

/** Localized POS catalog services (with categories) for the active app locale. */
export const getTutorialServices = (): Service[] => [...getMenu(tutorialLocale.value).services];

/** FoodTypes aren't needed for the demo; an empty list renders items ungrouped. */
export const TUTORIAL_FOOD_TYPES: FoodType[] = [];

// ---------------------------------------------------------------------------
// Orders (per busy table) — built fresh so the "time since first order" timer
// reads relative to now rather than a frozen timestamp. Items reference the
// same localized Product objects as the catalog, so names match everywhere.
// ---------------------------------------------------------------------------

// Swiss standard VAT (8.1%), attached to every demo order item so the printed
// bill and any tax breakdown render a VAT line instead of choking on missing tax.
// Prices are tax-inclusive (gross), matching how the bill template back-computes net.
const TUTORIAL_TAX_RATE: OrderItemTaxRate = {
  id: "tut-vat-standard",
  type: "percentage",
  value: 8.1,
};

let itemSeq = 1;
const mkItem = (product: Product, quantity: number): OrderItem => ({
  id: itemSeq++,
  order_id: 0,
  product_id: product.id,
  food_type_id: null,
  quantity,
  paid_quantity: null,
  price: parseFloat(product.price),
  notes: null,
  // Active (open) order item — `preparing` keeps it payable; `completed` would
  // read as already closed (see get_tables / create_payment_for_table SQL).
  status: "preparing",
  created_at: ISO,
  updated_at: ISO,
  deleted_at: null,
  product,
  bundles: null,
  tax_rate: TUTORIAL_TAX_RATE,
  tax_rate_id: TUTORIAL_TAX_RATE.id,
});

const mkOrder = (id: number, items: OrderItem[], minutesAgo: number): Order => {
  const created = minutesAgoISO(minutesAgo);
  items.forEach((it) => (it.order_id = id));
  const amount = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  return {
    id,
    guid: `tut-order-${id}`,
    restaurant_id: RESTAURANT_ID,
    table_id: null,
    user_id: FAKE_USER.id,
    service_method: "dine_in",
    discount_takeaway: null,
    display_id: id,
    parent_id: null,
    is_grouped: null,
    amount,
    tips: 0,
    partial_tips: null,
    delivery_fee: null,
    notes: null,
    payment_method: "",
    session_id: null,
    device_id: null,
    // Open orders on a busy table — `preparing` is one of the active statuses
    // (`new`/`preparing`/`ready`) the backend treats as payable; `completed`
    // would make the table read as already closed.
    status: "preparing",
    restaurant_status: "preparing",
    bar_status: "preparing",
    created_at: created,
    updated_at: created,
    items,
    user: FAKE_USER,
    is_multicourse: false,
    created_timestamp: created,
    updated_timestamp: created,
  };
};

// Order specs keyed by table id (only busy tables). Items are [productKey, qty]
// pairs resolved against the active-locale menu at read time.
type OrderSpec = { id: number; minutesAgo: number; items: [ProductKey, number][] };
const ORDER_SPECS: Record<number, OrderSpec[]> = {
  90101: [{ id: 910101, minutesAgo: 42, items: [["cola", 2], ["spaghetti", 1], ["salad", 1]] }],
  90102: [
    {
      id: 910201,
      minutesAgo: 28,
      items: [["redWine", 1], ["whiteWine", 1], ["fries", 1], ["cremeBrulee", 1]],
    },
  ],
  90103: [{ id: 910301, minutesAgo: 15, items: [["water", 2], ["spaghetti", 2], ["iceCream", 1]] }],
  90201: [{ id: 920101, minutesAgo: 9, items: [["iceTea", 1], ["soup", 1], ["fries", 1]] }],
};

export const getTutorialTableOrders = (tableId: number): Order[] => {
  const specs = ORDER_SPECS[tableId];
  if (!specs) return [];
  const { productByKey } = getMenu(tutorialLocale.value);
  return specs.map((o) =>
    mkOrder(
      o.id,
      o.items.map(([key, qty]) => mkItem(productByKey[key], qty)),
      o.minutesAgo,
    ),
  );
};

// The payment modal (TablePayment) fetches by order id via `getOrders`/`getTablePayments`,
// not by table id — so those need their own demo stubs. Without them the modal would hit
// the real backend with fake demo order ids (910101…) and fail to open.

/** Demo orders looked up by order id (mirrors `api.getOrders`, empty for unknown ids). */
export const getTutorialOrders = (orderIds?: number[]): Order[] => {
  if (!orderIds || orderIds.length === 0) return [];
  const wanted = new Set(orderIds);
  const { productByKey } = getMenu(tutorialLocale.value);
  const result: Order[] = [];
  for (const specs of Object.values(ORDER_SPECS)) {
    for (const o of specs) {
      if (!wanted.has(o.id)) continue;
      result.push(
        mkOrder(
          o.id,
          o.items.map(([key, qty]) => mkItem(productByKey[key], qty)),
          o.minutesAgo,
        ),
      );
    }
  }
  return result;
};

/** Demo tables have no recorded payments yet — the whole bill is unpaid. */
export const getTutorialTablePayments = (): {
  partial_payments: SuccesfullPartialPayment[];
  payments: PaymentDetails[];
} => ({ partial_payments: [], payments: [] });

// ---------------------------------------------------------------------------
// Tables — party names are famous 1990s Swiss personalities.
// ---------------------------------------------------------------------------

type TableDef = { id: number; name: string; party?: string };

// Interior: 5 tables, 3 busy (90101-90103). Terrace: 3 tables, 1 busy (90201).
const TABLE_DEFS: TableDef[] = [
  { id: 90101, name: "1", party: "Martina Hingis" },
  { id: 90102, name: "2", party: "Marc Rosset" },
  { id: 90103, name: "3", party: "Stéphane Chapuisat" },
  { id: 90104, name: "4" },
  { id: 90105, name: "5" },
  { id: 90201, name: "1", party: "DJ BoBo" },
  { id: 90202, name: "2" },
  { id: 90203, name: "3" },
];

export const getTutorialTables = (): TableStatus[] =>
  TABLE_DEFS.map((def) => {
    const orders = getTutorialTableOrders(def.id);
    const isBusy = orders.length > 0;
    return {
      id: def.id,
      table: {
        id: def.id,
        name: def.name,
        type: "",
        is_busy: isBusy ? 1 : 0,
        guid: `tut-table-${def.id}`,
      },
      party: isBusy && def.party ? { name: def.party } : undefined,
      orders: orders.map((o) => ({
        id: o.id,
        created_at: o.created_at,
        parent_id: null,
        status: o.status,
        shift_id: "tut-shift",
        user_id: FAKE_USER.id,
        display_id: o.display_id,
      })),
      total_amount: orders.reduce((sum, o) => sum + o.amount, 0),
      total_tips: 0,
      reservations: [],
    };
  });

// ---------------------------------------------------------------------------
// Reservations — a few upcoming, unseated bookings so the Orders / Reservations
// toggle has something to show. Left unassigned (empty table_assignments) so they
// surface as "to seat" without touching any table's busy/free state. Times are
// relative to now so they always read as upcoming.
// ---------------------------------------------------------------------------

const minutesFromNowISO = (mins: number) => new Date(Date.now() + mins * 60_000).toISOString();

type ReservationDef = { id: string; party: string; size: number; inMinutes: number };

const RESERVATION_DEFS: ReservationDef[] = [
  { id: "tut-res-1", party: "Roger Federer", size: 4, inMinutes: 30 },
  { id: "tut-res-2", party: "Lara Gut", size: 2, inMinutes: 75 },
  { id: "tut-res-3", party: "Didier Cuche", size: 6, inMinutes: 150 },
];

export const getTutorialReservations = (): Reservation[] =>
  RESERVATION_DEFS.map((def) => ({
    id: def.id,
    restaurant_id: RESTAURANT_ID,
    request_id: null,
    customer_id: null,
    customer_name: def.party,
    customer_email: null,
    time: minutesFromNowISO(def.inMinutes),
    duration_mins: 90,
    status: "confirmed",
    party_size: def.size,
    notes: null,
    party_name: def.party,
    party_email: `${def.id}@example.com`,
    party_phone: null,
    created_at: ISO,
    updated_at: ISO,
    deposit_amount: null,
    deposit_gateway_id: null,
    deposit_status: "none",
    table_assignments: [],
    comments: [],
    tags: [],
    source: "online",
  }));

// ---------------------------------------------------------------------------
// Floor plans — two rooms. Grid coords are 0-indexed inclusive cell positions;
// each table anchors at [data-row=rowStart][data-col=colStart] and spans
// (colEnd-colStart+1) x (rowEnd-rowStart+1) cells.
// ---------------------------------------------------------------------------

const mkCell = (
  id: string,
  tableId: number,
  colStart: number,
  rowStart: number,
  colEnd: number,
  rowEnd: number,
): FloorPlanTable => ({
  id,
  tableId,
  type: "table",
  shape: "rectangle",
  seats: 4,
  colStart,
  rowStart,
  colEnd,
  rowEnd,
});

export const TUTORIAL_FLOOR_PLANS: FloorPlan[] = [
  {
    id: "tut-interior",
    name: "Interior",
    color: "#1c2128",
    rows: 6,
    columns: 8,
    rank: 0,
    plan: [
      mkCell("tut-i-1", 90101, 0, 0, 1, 1),
      mkCell("tut-i-2", 90102, 3, 0, 4, 1),
      mkCell("tut-i-3", 90103, 6, 0, 7, 1),
      mkCell("tut-i-4", 90104, 0, 3, 1, 4),
      mkCell("tut-i-5", 90105, 3, 3, 4, 4),
    ],
  },
  {
    id: "tut-terrace",
    name: "Terrace",
    color: "#14532d",
    rows: 6,
    columns: 8,
    rank: 1,
    plan: [
      mkCell("tut-t-1", 90201, 0, 0, 1, 1),
      mkCell("tut-t-2", 90202, 3, 0, 4, 1),
      mkCell("tut-t-3", 90203, 6, 0, 7, 1),
    ],
  },
];
